import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonCard,
  IonCardContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  useIonToast,
} from "@ionic/react";
import { createOutline } from "ionicons/icons";
import React, { useContext, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { UdvData } from "../models/workorders/WorkOrder";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import useGetCustomValues from "../hooks/work-order/useGetCustomValues";
import useUpdateWo from "../hooks/work-order/useUpdateWo";
import { useNetworkStatusStore } from "../store/network-status";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import {
  getValidValueDisplayText,
  compareValidValuesByDisplayText,
} from "../util/DropdownDisplayModeHelper";

interface WorkOrderCustomFieldsProps {
  woNumber: string;
  customFields: { [key: string]: any } | undefined;
  customValues: UdvData[] | undefined;
  customFieldsErrorState: boolean;
  customValErrorState: boolean;
}

/**
 * One editable custom (user-defined) field on the WO detail screen. The value
 * column (StringValue / NumericValue / DateValue) is the field definition's
 * PropertyName, matching how the read-only view joins values by UdId.
 */
interface EditableCustomField {
  udId: string;
  key: string;
  def: any;
  label: string;
  valueColumn: keyof UdvData;
  category: string;
  subcategory: string;
  element: string;
  currentValue: string | number;
}

// Convert an EAM date string to the yyyy-MM-dd value an <IonInput type="date"> expects.
const toDateInputValue = (value: any): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const WorkOrderCustomFields: React.FC<WorkOrderCustomFieldsProps> = ({
  woNumber,
  customFields,
  customValues,
  customFieldsErrorState,
  customValErrorState,
}) => {
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { translations } = useContext(TranslationsContext);
  const isOnline = useNetworkStatusStore((state) => state.isOnline);
  // Requesters open the detail screen via the "requester" route (mob_requester_wo_nb);
  // only technicians (mob_wo_nb) may edit custom fields.
  const isRequester = useLocation().pathname.includes("requester");
  const canEdit = !isRequester;
  const { trigger } = useGetWorkOrder();
  const { getCustomValues } = useGetCustomValues();
  const { updateWo } = useUpdateWo();
  const [presentToast] = useIonToast();

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});

  // Flatten the custom-field definition map into an ordered, display-ready list.
  // The map key is "Category|Subcategory|Element"; fall back to the matching
  // UDV row (then to sensible defaults) so we can build the save payload for
  // fields that don't yet have a stored value.
  const fieldList = useMemo<EditableCustomField[]>(() => {
    if (!customFields) return [];
    return Object.entries(customFields).map(([key, def]) => {
      const parts = key.split("|");
      const hasKeyParts = parts.length === 3;
      const udv = customValues?.find((val) => val.UdId === def.UdId);
      const valueColumn = (def.PropertyName as keyof UdvData) || "StringValue";
      const rawValue = udv ? udv[valueColumn] : undefined;
      return {
        udId: def.UdId,
        key,
        def,
        label: def.TranslatedIdText || def.IdText,
        valueColumn,
        category: udv?.Category ?? (hasKeyParts ? parts[0] : "Work Order-All"),
        subcategory: udv?.Subcategory ?? (hasKeyParts ? parts[1] || " " : " "),
        element:
          udv?.Element ?? (hasKeyParts ? parts[2] || def.IdText : def.IdText),
        // Use || (not ??) so an unset numeric (stored as 0) renders blank, matching the
        // original detail-screen behavior. A genuine string "0" is truthy and preserved.
        currentValue: ((rawValue as string | number) || "") as string | number,
      };
    });
  }, [customFields, customValues]);

  const hasFields = fieldList.length > 0;
  const isDirty = JSON.stringify(editValues) !== JSON.stringify(initialValues);

  // Show the card when there is something to display: fields or an error.
  if (!customFieldsErrorState && !customValErrorState && !hasFields) {
    return null;
  }

  const beginEdit = () => {
    const initial: Record<string, any> = {};
    fieldList.forEach((field) => {
      initial[field.udId] =
        field.valueColumn === "DateValue"
          ? toDateInputValue(field.currentValue)
          : field.currentValue ?? "";
    });
    setEditValues(initial);
    setInitialValues(initial);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditValues({});
    setInitialValues({});
  };

  const buildUserDefinedValues = () =>
    fieldList.map((field) => {
      let value = editValues[field.udId];
      if (value === "" || value === undefined) {
        value = null;
      } else if (field.valueColumn === "NumericValue") {
        const num = Number(value);
        value = isNaN(num) ? null : num;
      }
      return {
        Category: field.category,
        Subcategory: field.subcategory,
        Element: field.element,
        [field.valueColumn]: value,
      };
    });

  const handleSave = async () => {
    // Required-field validation.
    const missingRequired = fieldList.filter((field) => {
      const value = editValues[field.udId];
      return (
        field.def.IsRequired &&
        (value === "" || value === undefined || value === null)
      );
    });
    if (missingRequired.length > 0) {
      presentToast({
        message:
          translatedMessages["errIsRequired"]?.MessageText?.replace(
            "@1",
            missingRequired[0].label
          ) || `${missingRequired[0].label} is required`,
        duration: 3000,
        color: "danger",
      });
      return;
    }

    setSaving(true);
    try {
      // Re-fetch the work order to get a current RowVersion and avoid a
      // concurrency mismatch (same approach as EquipmentResults).
      const woResponse = await trigger(woNumber);
      if (woResponse.isError || !woResponse.data) {
        presentToast({
          message:
            translatedMessages["ErrUpdateWorkOrder"]?.MessageText ||
            "Unable to update the work order.",
          duration: 3000,
          color: "danger",
        });
        return;
      }

      const payload = {
        Confirmation: { RuleConfirmationDetailsList: [] },
        RequestObject: {
          ...woResponse.data,
          UserDefinedValues: buildUserDefinedValues(),
        },
      };

      const result = await updateWo(payload);
      if (!result.success) {
        presentToast({
          message:
            result.message ||
            translatedMessages["ErrUpdateWorkOrder"]?.MessageText ||
            "Unable to update the work order.",
          duration: 4000,
          color: "danger",
        });
        return;
      }

      // Refresh custom values and the work order so the read-only view reflects
      // the saved changes.
      await getCustomValues(woNumber);
      await trigger(woNumber);

      presentToast({
        message:
          translatedMessages["msgWorkOrderUpdated"]?.MessageText ||
          "Work order updated.",
        duration: 2000,
        color: "success",
      });
      setEditMode(false);
    } catch (e) {
      presentToast({
        message:
          translatedMessages["ErrUpdateWorkOrder"]?.MessageText ||
          "Unable to update the work order.",
        duration: 3000,
        color: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderReadValue = (field: EditableCustomField) => {
    const value = field.currentValue;
    if (field.valueColumn === "DateValue" && value !== "") {
      return new Date(value).getFullYear() <= 1
        ? ""
        : new Date(value).toLocaleDateString();
    }
    return String(value);
  };

  const renderEditControl = (field: EditableCustomField) => {
    const def = field.def;
    const isProtected = def.IsProtected === true;
    const value = editValues[field.udId] ?? "";

    if (def.ValidValues && def.ValidValues.length > 0) {
      return (
        <IonSelect
          interface="popover"
          value={value}
          disabled={isProtected}
          onIonChange={(ev) =>
            setEditValues((current) => ({
              ...current,
              [field.udId]: ev.detail.value,
            }))
          }
        >
          <IonSelectOption value={null}> </IonSelectOption>
          {[...def.ValidValues]
            .sort((a: any, b: any) =>
              compareValidValuesByDisplayText(a, b, def.DropdownDisplayMode)
            )
            .map((option: any, index: number) => (
              <IonSelectOption
                value={option.Code}
                key={`${field.udId}-option-${index}`}
              >
                {getValidValueDisplayText(option, def.DropdownDisplayMode)}
              </IonSelectOption>
            ))}
        </IonSelect>
      );
    }

    const type =
      field.valueColumn === "DateValue"
        ? "date"
        : field.valueColumn === "NumericValue"
        ? "number"
        : "text";

    return (
      <IonInput
        type={type}
        value={value}
        readonly={isProtected}
        onIonInput={(ev: any) =>
          setEditValues((current) => ({
            ...current,
            [field.udId]: ev.detail.target.value,
          }))
        }
      ></IonInput>
    );
  };

  return (
    <IonCard>
      <IonLoading
        isOpen={saving}
        message={translations["lbl_saving"] || "Saving..."}
      />
      <IonAccordionGroup>
        <IonAccordion value="customFields">
          <IonItem slot="header" color="light">
            <IonLabel>
              {translations["lbl_grp_cust_info"] || "Custom Information"}
            </IonLabel>
          </IonItem>
          <div slot="content">
            <IonCardContent>
              {customFieldsErrorState ? (
                <IonText
                  color={"danger"}
                  style={{ padding: "16px", display: "inline-block" }}
                >
                  {translatedMessages["ErrCustomFields"]?.MessageText ||
                    "Error retrieving custom fields"}
                </IonText>
              ) : (
                <>
                  {hasFields && canEdit && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "8px",
                        padding: "8px 0 0",
                      }}
                    >
                      {editMode ? (
                        <>
                          <IonButton
                            fill="outline"
                            size="small"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            {translations["lbl_btn_cancel"] || "Cancel"}
                          </IonButton>
                          <IonButton
                            fill="solid"
                            size="small"
                            onClick={handleSave}
                            disabled={saving || !isDirty}
                          >
                            {saving ? (
                              <IonSpinner name="dots" />
                            ) : (
                              translations["lbl_btn_save"] || "Save"
                            )}
                          </IonButton>
                        </>
                      ) : (
                        <IonButton
                          fill="solid"
                          size="small"
                          onClick={beginEdit}
                          disabled={!isOnline}
                        >
                          <IonIcon slot="start" icon={createOutline} />
                          {translations["lbl_btn_edit"] || "Edit"}
                        </IonButton>
                      )}
                    </div>
                  )}

                  {!editMode && !isOnline && hasFields && canEdit && (
                    <IonNote
                      color="medium"
                      style={{ padding: "0 16px", display: "block" }}
                    >
                      {translatedMessages["msgEditRequiresOnline"]
                        ?.MessageText ||
                        "Editing is available when you are online."}
                    </IonNote>
                  )}

                  <IonList>
                    {customValErrorState && (
                      <IonItem lines="none" key={`custom-tab-error-item`}>
                        <IonText color={"danger"} key={"custom-tab-error-text"}>
                          {translatedMessages["ErrCustomValues"]?.MessageText ||
                            "Error fetching custom values"}
                        </IonText>
                      </IonItem>
                    )}
                    {fieldList.map((field) => (
                      <IonItem lines="full" key={`custom-field-${field.udId}`}>
                        <IonLabel position={editMode ? "stacked" : undefined}>
                          <p>
                            {field.label}
                            {editMode && field.def.IsRequired ? " *" : ""}
                          </p>
                          {!editMode && <h6>{renderReadValue(field)}</h6>}
                        </IonLabel>
                        {editMode && renderEditControl(field)}
                      </IonItem>
                    ))}
                  </IonList>
                </>
              )}
            </IonCardContent>
          </div>
        </IonAccordion>
      </IonAccordionGroup>
    </IonCard>
  );
};

export default WorkOrderCustomFields;
